import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';
import { EmojiPicker } from '@/components/emoji-picker/emoji-picker';
import type { EmojiRef } from '@/components/emoji-renderer/types';

export interface EditorEmojiPickerProps {
  editor: Editor;
}

/**
 * Emoji picker — opens when `:` is typed at a word boundary (start of line, or
 * after whitespace). Mirrors the legacy emoji-picker-plugin: a caret-anchored
 * FloatingPanel with the shared EmojiPicker; on select, the `:` is removed and
 * the emoji node is inserted via the EmojiExtension's `insertEmoji` command.
 */
export function EditorEmojiPicker({ editor }: EditorEmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  // Doc position of the `:` that opened the picker, so we can delete it on select.
  const colonPosRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Detect `:` typed at a word boundary → open the picker.
  useEffect(() => {
    const handleUpdate = () => {
      if (open) return;
      const { state } = editor;
      const { selection } = state;
      if (!selection.empty) return;
      const { $from } = selection;
      const textBefore = $from.nodeBefore?.textContent ?? '';
      if (!textBefore.endsWith(':')) return;
      // Word boundary: line start, or the char before `:` is whitespace.
      const beforeColon = textBefore.slice(0, -1);
      if (beforeColon.length > 0 && !/\s$/.test(beforeColon)) return;

      colonPosRef.current = $from.pos;
      const coords = editor.view.coordsAtPos($from.pos);
      setPos({ top: coords.bottom + 4, left: coords.left });
      setOpen(true);
    };
    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, open]);

  const close = () => setOpen(false);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const handleSelect = (ref: EmojiRef) => {
    const colonPos = colonPosRef.current;
    const chain = editor.chain().focus();
    // Remove the trigger `:` (one char before its recorded position) first.
    if (colonPos !== null) chain.deleteRange({ from: colonPos - 1, to: colonPos });
    chain.insertEmoji(ref).run();
    close();
  };

  if (!open) return null;

  return (
    <FloatingPanel ref={panelRef} top={pos.top} left={pos.left}>
      <EmojiPicker onSelect={handleSelect} defaultVariant="default" showVariantToggle={false} />
    </FloatingPanel>
  );
}
